import { Flex, JWTRole, Spacer } from "@hackthenorth/north";
import { XMarkIcon } from "@heroicons/react/24/outline";
import _ from "lodash";
import {
  Button,
  ButtonGroup,
  Checkbox,
  // DropdownMenu,
  DropdownMenuItem,
  Overlay,
  Select,
  Table,
  Text,
  TextInput,
} from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import styled from "styled-components";

import { useGenerateBulkResumeZipDownloadMutation } from "src/api/sponsors/generateBulkResumeZipDownload.generated";
import { useGenerateSponsorshipHackerRecruitmentCsvLinkMutation } from "src/api/sponsors/generateSponsorshipHackerRecruitmentCsvLink.generated";
import { useGetAllHackersQuery } from "src/api/sponsors/getAllHackers.generated";
import { useGetCompanyNetworkingScansQuery } from "src/api/sponsors/getCompanyNetworkingScans.generated";
import { errorToast, Icon, Spinner, successToast } from "src/shared/components";
import Header, { TOPNAVBAR_HEIGHT } from "src/shared/components/Header";
import { IconName } from "src/shared/components/Icon";
import { useSponsorContext, useUserContext } from "src/shared/contexts";
import { configurePdfWorker } from "src/shared/utils/pdfWorker";
import { answersToNorthV2Options } from "src/shared/utils/react-select";
import globalConstants from "src/theme/globalConstants";

// Autosave, Shipping Swag, Table Select, Sponsor T&C, Merge Conflicts
import { usePerkState } from "../perks/components/Perk/usePerkState";
import { useSponsorPerksContext } from "../perks/SponsorPerksContext";
import { SponsorPerkType } from "../perks/types";

import { THackerProfile } from "./components/HackerProfile/types";
import { PROGRAM_OPTIONS, SCHOOL_OPTIONS } from "./constants";
import {
  parseHackerProfiles,
  THackerFilter,
  TAcceptanceStatus,
  getAcceptanceStatus,
  getApplicationStages,
  getFilteredHackerIds,
  hackerExpressedInterestInCompany,
} from "./utils";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
// import { useGetHackerCsvQuery } from "./graphql/getHackerCSV.generated";

// React PDF Config
configurePdfWorker();

// Constants
const DEFAULT_NUM_PROFILES = 10; // Keep in mind that file limit on HAPI is 10
const DEFAULT_SCHOOL_TO_ADD_ON_FILTER = "The University of Waterloo";
const DEFAULT_PROGRAM_TO_ADD_ON_FILTER = "Computer Science";
const CURRENT_SPONSORSHIP_RESUME_GENERATION_YEAR = 2026;
const RECRUITMENT_SUBTITLE =
  "View our database of hackers interested in career opportunities";

const SponsorRecruitment = () => {
  // pdf viewer
  const [overlayOpen, setOverlayOpen] = useState<boolean>(false);
  const [numPDFPages, setNumPDFPages] = useState<null | number>(null);
  const [pagePDFNumber, setPagePDFNumber] = useState<number>(1);
  const [selectedPDFUri, setSelectedPDFUri] = useState<string>("");
  const [selectedHackerId, setSelectedHackerId] = useState<string>("");

  // pagination
  const [skipped, setSkipped] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_NUM_PROFILES);

  // filters
  const [filters, setFilters] = useState<THackerFilter[]>([]);
  const [query, setQuery] = useState<string>("");
  const [laggedQuery, setLaggedQuery] = useState<string>("");
  const [schools, setSchools] = useState<string[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [starred, setStarred] = useState<boolean>(false);
  const [scanned, setScanned] = useState<boolean>(false);
  const [interested, setInterested] = useState<boolean>(false);
  const [acceptanceStatus, setAcceptanceStatus] =
    useState<TAcceptanceStatus>("all");
  const [colsNotVisible, setColsNotVisible] = useState<string[]>([]);
  const [gradYearRange, setGradYearRange] = useState<number[]>([2000, 2099]);

  const [getHackerCsvFile, { loading: getHackerCsvFileLoading }] =
    useGenerateSponsorshipHackerRecruitmentCsvLinkMutation({
      variables: {
        year: CURRENT_SPONSORSHIP_RESUME_GENERATION_YEAR,
      },
    });

  const [getHackerResumeZipFile, { loading: getHackerZipFileLoading }] =
    useGenerateBulkResumeZipDownloadMutation({
      variables: {
        year: CURRENT_SPONSORSHIP_RESUME_GENERATION_YEAR,
        forceRegenerate: false,
      },
    });

  const { company } = useSponsorContext();
  const { roles } = useUserContext();
  const { perks, perksLoaded, loadingPerks, loadError } =
    useSponsorPerksContext();
  const isSponsor = roles.includes(JWTRole.SPONSOR);
  const hasEarlyResumeAccess = perks?.available_perks?.value?.includes(
    "early_hacker_resume"
  );
  const hasPostEventResumeAccess = perks?.available_perks?.value?.includes(
    "post_event_hacker_resume"
  );
  const isPostEvent =
    company?.event.end_time !== undefined &&
    new Date() >= new Date(company.event.end_time);
  const resumeAccess =
    hasEarlyResumeAccess || (hasPostEventResumeAccess && isPostEvent);

  // Get company networking scans
  const { data: networkingScansData } = useGetCompanyNetworkingScansQuery();
  const { getState, updateState, refetchPerks } = usePerkState({
    perk: SponsorPerkType.COFFEE_CHATS,
    autosaveDelayMs: 250,
    onSaveSuccess: () => {
      successToast("Starred hacker saved successfully!");
      refetchPerks();
    },
  });

  // const { data: hackerCSVData } = useGetHackerCsvQuery();

  const favoritedHackers: string[] =
    getState("coffee_chats_favorited_hackers") ?? [];
  const favoritedHackerMap = _.keyBy(favoritedHackers);

  // Create a set of scanned hacker IDs for easy lookup
  const scannedHackerSet = useMemo(() => {
    if (!networkingScansData?.getCompanyNetworkingScans)
      return new Set<number>();
    const ids = networkingScansData.getCompanyNetworkingScans
      .map((scan) => scan.other_claim_id)
      .filter((id) => id !== null && id !== undefined) as number[];
    return new Set(ids);
  }, [networkingScansData]);

  // Create a mapping of scanned hacker IDs to all representatives who scanned them
  const scannedHackerToScannerMap = useMemo(() => {
    if (!networkingScansData?.getCompanyNetworkingScans)
      return new Map<number, string[]>();
    const map = new Map<number, string[]>();
    networkingScansData.getCompanyNetworkingScans.forEach((scan) => {
      if (scan.other_claim_id && scan.representative_name) {
        const existingScanners = map.get(scan.other_claim_id) || [];
        if (!existingScanners.includes(scan.representative_name)) {
          map.set(scan.other_claim_id, [
            ...existingScanners,
            scan.representative_name,
          ]);
        }
      }
    });
    return map;
  }, [networkingScansData]);

  const onFavorite = (hackerId: string) => {
    const isFavorited = favoritedHackers.includes(hackerId);
    updateState(
      "coffee_chats_favorited_hackers",
      isFavorited
        ? favoritedHackers.filter((id) => id !== hackerId)
        : [...favoritedHackers, hackerId]
    );
  };

  useEffect(() => {
    const filterData: THackerFilter[] = [];
    if (starred)
      filterData.push({
        type: "starred",
        value: true,
      });
    if (scanned)
      filterData.push({
        type: "scanned",
        value: true,
      });
    if (interested)
      filterData.push({
        type: "interested",
        value: true,
      });
    if (schools.length > 0)
      filterData.push({
        type: "school",
        value: schools,
      });
    if (programs.length > 0)
      filterData.push({
        type: "program",
        value: programs,
      });
    if (gradYearRange[0] !== 2000 && gradYearRange[1] !== 2099) {
      filterData.push({
        type: "gradYear",
        value: gradYearRange.map(String),
      });
    }
    setSkipped(0);
    setFilters(filterData);
  }, [schools, programs, gradYearRange, starred, scanned, interested]);

  // Get all starred hacker IDs
  const starredHackerIds = useMemo(() => {
    const ids = favoritedHackers
      .map((id) => parseInt(id))
      .filter((id) => !isNaN(id));
    return ids;
  }, [favoritedHackers]);

  // Get all scanned hacker IDs from networking scans
  const scannedHackerIds = useMemo(() => {
    if (!networkingScansData?.getCompanyNetworkingScans) return [];
    const ids = networkingScansData.getCompanyNetworkingScans
      .map((scan) => scan.other_claim_id)
      .filter((id) => id !== null && id !== undefined) as number[];
    return ids;
  }, [networkingScansData]);

  // Get the filtered hacker IDs based on current filters
  const filteredHackerIds = useMemo(() => {
    return getFilteredHackerIds(filters, starredHackerIds, scannedHackerIds);
  }, [filters, starredHackerIds, scannedHackerIds]);

  const { data, loading: fetchingHackers } = useGetAllHackersQuery({
    variables: {
      skip: skipped,
      take: itemsPerPage,
      stages: getApplicationStages(acceptanceStatus),
      schools: schools.length ? schools : undefined,
      programs: programs.length ? programs : undefined,
      graduatingYearMin:
        gradYearRange[0] === 2000 ? undefined : gradYearRange[0],
      graduatingYearMax:
        gradYearRange[1] === 2099 ? undefined : gradYearRange[1],
      query: laggedQuery.trim() || undefined,
      claimIds: filteredHackerIds ?? undefined,
      interested,
    },
    skip: !resumeAccess,
  });
  const hasActiveRecruitmentFilters =
    filters.length > 0 ||
    laggedQuery.trim().length > 0 ||
    acceptanceStatus !== "all";

  // Stores all hacker profiles
  const [hackerProfiles, setHackerProfiles] = useState<THackerProfile[]>([]);
  const totalCount = data?.getSponsorRecruitmentCandidates.totalCount ?? 0;

  useEffect(() => {
    if (data && !fetchingHackers) {
      const newProfiles = parseHackerProfiles(data);
      setHackerProfiles(newProfiles);
    }
  }, [data, fetchingHackers, skipped]);

  const paginationRange = hackerProfiles.length
    ? `${skipped + 1}–${skipped + hackerProfiles.length} of ${totalCount}`
    : `0 of ${totalCount}`;

  const handleFetchMoreHackers = (isForward: boolean) => {
    setSkipped((prevSkipped) => {
      if (isForward) {
        return prevSkipped + itemsPerPage;
      } else {
        return Math.max(0, prevSkipped - itemsPerPage);
      }
    });
  };

  const tableData = hackerProfiles.map((profile: THackerProfile) => {
    const iconName: IconName = favoritedHackerMap[profile.id.toString()]
      ? "selected-star"
      : "not-selected-star";
    const isScanned = scannedHackerSet.has(profile.id);
    const isInterested = hackerExpressedInterestInCompany(
      profile.sponsorInterests,
      company?.name
    );
    const profileAcceptanceStatus = getAcceptanceStatus(
      profile.applicationStage
    );
    const acceptanceStatusConfig = {
      pending: { color: "#F59E0B", label: "Pending" },
      accepted: { color: "#10B981", label: "Accepted" },
      rejected: { color: "#EF4444", label: "Not accepted" },
    }[profileAcceptanceStatus];
    return {
      star: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon
            style={{ cursor: "pointer" }}
            name={iconName}
            onClick={() => onFavorite(profile.id.toString())}
          />
        </div>
      ),
      scanned: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <HoverTooltipWithTooltip>
            {isScanned ? (
              <Icon name="profile-badge" size="16px" color="#10B981" />
            ) : (
              <Icon name="profile-badge" size="16px" color="#9CA3AF" />
            )}
            <TooltipText>
              {isScanned
                ? (() => {
                    const scanners =
                      scannedHackerToScannerMap.get(profile.id) || [];
                    if (scanners.length === 0) {
                      return "Scanned by Unknown";
                    } else if (scanners.length === 1) {
                      return `Scanned by ${scanners[0]}`;
                    } else {
                      return `Scanned by ${scanners.join(", ")}`;
                    }
                  })()
                : "Not scanned"}
            </TooltipText>
          </HoverTooltipWithTooltip>
        </div>
      ),
      interested: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <HoverTooltipWithTooltip>
            {isInterested ? (
              <Icon
                name="check-circle-inverted-green"
                size="16px"
                color="#10B981"
              />
            ) : (
              <Icon name="empty-checkmark" size="16px" color="#9CA3AF" />
            )}
            <TooltipText>
              {isInterested
                ? `Interested in ${company?.name ?? "your company"}`
                : "Not interested"}
            </TooltipText>
          </HoverTooltipWithTooltip>
        </div>
      ),
      acceptanceStatus: (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <HoverTooltipWithTooltip>
            <StatusCircle color={acceptanceStatusConfig.color} />
            <TooltipText>{acceptanceStatusConfig.label}</TooltipText>
          </HoverTooltipWithTooltip>
        </div>
      ),
      name: profile?.name,
      email: profile?.email,
      school: profile?.school,
      program: profile?.program,
      levelOfStudy: _.startCase(_.toLower(profile?.levelOfStudy)),
      graduationYear: profile?.graduatingYear.toString(),
      resume: (
        <Button
          color="secondary"
          size="sm"
          disabled={!profile?.resume?.uri}
          onClick={() => {
            setSelectedPDFUri(profile?.resume?.uri ?? "");
            setSelectedHackerId(profile?.id.toString());
            setOverlayOpen(true);
          }}
        >
          View
        </Button>
      ),
    };
  });

  const tableColumns = [
    {
      id: "star",
      name: "Favourited",
      hideLabel: false,
      align: "center" as const,
      omit: colsNotVisible.includes("Favourited"),
    },
    {
      id: "scanned",
      name: "Scanned",
      hideLabel: false,
      align: "center" as const,
      omit: colsNotVisible.includes("Scanned"),
    },
    {
      id: "interested",
      name: "Interested",
      hideLabel: false,
      align: "center" as const,
      omit: colsNotVisible.includes("Interested"),
    },
    {
      id: "acceptanceStatus",
      name: "Acceptance Status",
      hideLabel: false,
      align: "center" as const,
      omit: colsNotVisible.includes("Acceptance Status"),
    },
    {
      id: "name",
      name: "Name",
      omit: colsNotVisible.includes("Name"),
    },
    {
      id: "email",
      name: "Email",
      omit: colsNotVisible.includes("Email"),
    },
    {
      id: "school",
      name: "School",
      omit: colsNotVisible.includes("School"),
    },
    {
      id: "program",
      name: "Program",
      omit: colsNotVisible.includes("Program"),
    },
    {
      id: "levelOfStudy",
      name: "Level of Study",
      omit: colsNotVisible.includes("Level of Study"),
    },
    {
      id: "graduationYear",
      name: "Graduation Year",
      omit: colsNotVisible.includes("Graduation Year"),
    },
    {
      id: "resume",
      name: "Resume",
      omit: colsNotVisible.includes("Resume"),
    },
  ];

  const schoolMenuItems: DropdownMenuItem[] = [];
  const programMenuItems: DropdownMenuItem[] = [];
  const columnsMenuItems: DropdownMenuItem[] = [];
  const gradYearMenuItems: DropdownMenuItem[] = [];

  const schoolNorthOptions = useMemo(
    () => answersToNorthV2Options(SCHOOL_OPTIONS),
    []
  );
  const programNorthOptions = useMemo(
    () => answersToNorthV2Options(PROGRAM_OPTIONS),
    []
  );
  const gradYearNorthOptions = useMemo(
    () => answersToNorthV2Options(_.range(2000, 2100).map(String)),
    []
  );
  const setupDropdown = () => {
    // Setup School and Program dropdown options
    const dropdownTitle = (title: string) => ({
      content: <EmptyFilterTitleText>{title}</EmptyFilterTitleText>,
      action: () => {},
      disabled: true,
    });
    const dropdownNoFiltersMessage = (message: string) => ({
      content: <EmptyFilterDescText>{message}</EmptyFilterDescText>,
      action: () => {},
      disabled: true,
    });
    const dropdownAddFilter = (onClick: () => void, itemName: string) => ({
      content: (
        <Button
          color="tertiary"
          leadingIcon={<Icon name="spon-plus" size="12px" />}
          size="md"
          style={{
            color: (globalConstants?.color?.textPrimary as string) ?? "",
          }}
          onClick={onClick}
        >
          Add a {itemName}
        </Button>
      ),
      action: () => {},
      disabled: true,
    });

    const dropdownFilters = [
      {
        title: dropdownTitle("School"),
        noFiltersMessage: dropdownNoFiltersMessage(
          "No schools added! Add a school using the button below to view students from that school."
        ),
        addFilter: dropdownAddFilter(
          () => setSchools([...schools, DEFAULT_SCHOOL_TO_ADD_ON_FILTER]),
          "school"
        ),
        items: {
          state: schools,
          setState: setSchools,
          options: schoolNorthOptions,
        },
        menuItems: schoolMenuItems,
      },
      {
        title: dropdownTitle("Program"),
        noFiltersMessage: dropdownNoFiltersMessage(
          "No programs added! Add a program using the button below to view students from that program."
        ),
        addFilter: dropdownAddFilter(
          () => setPrograms([...programs, DEFAULT_PROGRAM_TO_ADD_ON_FILTER]),
          "program"
        ),
        items: {
          state: programs,
          setState: setPrograms,
          options: programNorthOptions,
        },
        menuItems: programMenuItems,
      },
    ];

    dropdownFilters.forEach(
      ({ title, noFiltersMessage, addFilter, items, menuItems }) => {
        if (title) menuItems.push(title);
        if (noFiltersMessage && items.state.length === 0)
          menuItems.push(noFiltersMessage);
        items.state.forEach((item: string, idx: number) => {
          const dropdownItem = (
            <Select
              size="sm"
              onChange={(e) => {
                const newState = [...items.state];
                newState[idx] = e.target.value;
                items.setState(newState);
              }}
              value={item}
            >
              {items.options}
            </Select>
          );
          const deleteButton = (
            <XMarkIcon
              className="w-4 h-4 cursor-pointer text-gray-400"
              onClick={() => {
                const newState = [...items.state];
                newState.splice(idx, 1);
                items.setState(newState);
              }}
            />
          );
          menuItems.push({
            content: (
              <div className="flex items-center justify-between gap-2">
                {dropdownItem}
                {deleteButton}
              </div>
            ),
            action: () => {},
            disabled: true,
          });
        });
        if (addFilter) menuItems.push(addFilter);
      }
    );

    // Setup Column Filter
    columnsMenuItems.push(dropdownTitle("Toggle column visibility"));
    tableColumns.forEach(({ name }) => {
      columnsMenuItems.push({
        content: (
          <Checkbox
            checked={colsNotVisible.includes(name)}
            onChange={() =>
              !colsNotVisible.includes(name)
                ? setColsNotVisible([...colsNotVisible, name])
                : setColsNotVisible(colsNotVisible.filter((v) => v !== name))
            }
            label={name}
          />
        ),
        action: () => {},
        disabled: true,
      });
    });

    // Setup Grad Year Filter
    gradYearMenuItems.push(dropdownTitle("Graduation Year"));
    gradYearMenuItems.push({
      content: (
        <GradYearFilterSelect>
          From<Spacer width={4}></Spacer>
          <Select
            size="sm"
            onChange={(e) => {
              const newState = [...gradYearRange];
              newState[0] = parseInt(e.target.value);
              setGradYearRange(newState);
            }}
            value={gradYearRange[0]}
            error={gradYearRange[0] > gradYearRange[1] && "Range Error"}
          >
            {gradYearNorthOptions}
          </Select>
        </GradYearFilterSelect>
      ),
      action: () => {},
      disabled: true,
    });
    gradYearMenuItems.push({
      content: (
        <GradYearFilterSelect>
          To<Spacer width={4}></Spacer>
          <Select
            size="sm"
            onChange={(e) => {
              const newState = [...gradYearRange];
              newState[1] = parseInt(e.target.value);
              setGradYearRange(newState);
            }}
            value={gradYearRange[1]}
          >
            {gradYearNorthOptions}
          </Select>
        </GradYearFilterSelect>
      ),
      action: () => {},
      disabled: true,
    });
  };

  const handleClickHackerResumeCsvDownload = async () => {
    try {
      const file = await getHackerCsvFile();
      const uri = file.data?.generateSponsorshipHackerRecruitmentCsvLink?.uri;
      if (uri) {
        window.open(uri, "_blank");
      }
    } catch (error) {
      errorToast(
        "No consented hacker data is currently available to download."
      );
    }
  };

  const handleClickHackerResumeZipDownload = async () => {
    try {
      const file = await getHackerResumeZipFile();
      if (file.data?.generateBulkResumeZipDownload?.uri) {
        window.open(file.data.generateBulkResumeZipDownload.uri, "_blank");
      }
    } catch (error) {
      errorToast("Error downloading bulk resume ZIP, please try again.");
    }
  };

  setupDropdown();

  const hackerResumesUnavailable =
    !fetchingHackers && tableData.length === 0 && !hasActiveRecruitmentFilters;

  return (
    <>
      <Header
        title="Recruitment"
        subtitle={RECRUITMENT_SUBTITLE}
        actionButton={
          <Flex align="center" style={{ gap: "12px" }}>
            <Button
              leadingIcon={
                <Icon name="spon-download" size="14px" color="rgba(0,0,0,0)" />
              }
              color="primary"
              onClick={handleClickHackerResumeCsvDownload}
              disabled={
                !resumeAccess ||
                hackerResumesUnavailable ||
                getHackerCsvFileLoading
              }
            >
              {getHackerCsvFileLoading ? "Loading..." : "Download CSVs"}
            </Button>
            <Button
              leadingIcon={
                <Icon name="spon-download" size="14px" color="rgba(0,0,0,0)" />
              }
              color="primary"
              onClick={handleClickHackerResumeZipDownload}
              disabled={
                !resumeAccess ||
                hackerResumesUnavailable ||
                getHackerZipFileLoading
              }
            >
              {getHackerZipFileLoading
                ? "Generating ZIP..."
                : "Download All Resumes"}
            </Button>
          </Flex>
        }
      />
      {!isSponsor ? (
        <Container>
          <Text type="subtitle" style={{ color: "#666" }}>
            Your account isn&apos;t currently connected to a sponsor company.
            Make sure you&apos;re added to a sponsor company before visiting
            this page.
          </Text>
        </Container>
      ) : loadingPerks || !perksLoaded ? (
        <Container>
          <div
            style={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Spinner text="Loading recruitment access..." />
          </div>
        </Container>
      ) : loadError ? (
        <Container>
          <Text type="subtitle" style={{ color: "#666" }}>
            We couldn&apos;t load your recruitment access. Please refresh and
            try again.
          </Text>
        </Container>
      ) : resumeAccess ? (
        <>
          {hackerResumesUnavailable ? (
            <Container>
              <Text type="subtitle" style={{ color: "#666" }}>
                Hacker resumes will be available here shortly.
              </Text>
            </Container>
          ) : (
            <Container>
              <div style={{ width: "100%" }}>
                <div
                  className="flex justify-between mb-4"
                  style={{ alignItems: "center" }}
                >
                  <SearchContainer>
                    <TextInput
                      type="text"
                      placeholder="Search for someone..."
                      leadingIcon={<Icon name="search" />}
                      onChange={(e: any) => setQuery(e.target.value)}
                      value={query}
                      disabled={fetchingHackers}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setSkipped(0);
                          setLaggedQuery(query);
                        }
                      }}
                    />
                    <Button
                      color="primary"
                      size="md"
                      onClick={() => {
                        setSkipped(0);
                        setLaggedQuery(query);
                      }}
                    >
                      Search
                    </Button>
                  </SearchContainer>
                  <FilterContainer>
                    {/*<DropdownFilters>
                    <FilterDropdown
                      selectWidth={165}
                      items={[columnsMenuItems]}
                    >
                      <Button
                        trailingIcon={
                          <Icon
                            name="down-arrow"
                            color="rgba(0,0,0,0)"
                            size="12px"
                          />
                        }
                        disabled={fetchingHackers}
                      >
                        <DropdownButtonText type="subtitle">
                          Columns
                        </DropdownButtonText>
                      </Button>
                    </FilterDropdown>
                    <FilterDropdown
                      selectWidth={145}
                      items={[gradYearMenuItems]}
                    >
                      <Button
                        trailingIcon={
                          <Icon
                            name="down-arrow"
                            color="rgba(0,0,0,0)"
                            size="12px"
                          />
                        }
                        disabled={fetchingHackers}
                      >
                        <DropdownButtonText type="subtitle">
                          Graduation Year
                        </DropdownButtonText>
                      </Button>
                    </FilterDropdown>
                    <FilterDropdown
                      selectWidth={165}
                      items={[programMenuItems]}
                    >
                      <Button
                        trailingIcon={
                          <Icon
                            name="down-arrow"
                            color="rgba(0,0,0,0)"
                            size="12px"
                          />
                        }
                        disabled={fetchingHackers}
                      >
                        <DropdownButtonText type="subtitle">
                          Filter by program
                        </DropdownButtonText>
                      </Button>
                    </FilterDropdown>
                    <FilterDropdown selectWidth={165} items={[schoolMenuItems]}>
                      <Button
                        trailingIcon={
                          <Icon
                            name="down-arrow"
                            color="rgba(0,0,0,0)"
                            size="12px"
                          />
                        }
                        disabled={fetchingHackers}
                      >
                        <DropdownButtonText type="subtitle">
                          Filter by school
                        </DropdownButtonText>
                      </Button>
                    </FilterDropdown>
                  </DropdownFilters>*/}
                    <Select
                      size="sm"
                      value={acceptanceStatus}
                      onChange={(e) => {
                        setSkipped(0);
                        setAcceptanceStatus(
                          e.target.value as TAcceptanceStatus
                        );
                      }}
                      disabled={fetchingHackers}
                    >
                      <Select.Option value="all">
                        All acceptance statuses
                      </Select.Option>
                      <Select.Option value="pending">Pending</Select.Option>
                      <Select.Option value="accepted">Accepted</Select.Option>
                      <Select.Option value="rejected">
                        Not accepted
                      </Select.Option>
                    </Select>
                    <Checkbox
                      onChange={() => setStarred(!starred)}
                      checked={starred}
                      label="Only show starred"
                      size={16}
                      disabled={fetchingHackers}
                    />
                    <Checkbox
                      onChange={() => setScanned(!scanned)}
                      checked={scanned}
                      label="Only show scanned"
                      size={16}
                      disabled={fetchingHackers}
                    />
                    <Checkbox
                      onChange={() => setInterested(!interested)}
                      checked={interested}
                      label="Only show interested in us"
                      size={16}
                      disabled={fetchingHackers}
                    />
                  </FilterContainer>
                </div>
                <>
                  {fetchingHackers ? (
                    <Spinner text="Loading candidates..." />
                  ) : filteredHackerIds?.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <Text
                        type="subtitle"
                        style={{ display: "block", marginBottom: "10px" }}
                      >
                        No hackers found
                      </Text>
                      <Text type="body1" style={{ color: "#666" }}>
                        {starred && scanned
                          ? "No hackers found that are both starred and scanned."
                          : starred
                          ? "Star some hackers to see them here when filtering by starred."
                          : "Scan some hackers at the event to see them here when filtering by scanned."}
                      </Text>
                    </div>
                  ) : tableData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <Text type="subtitle">
                        No hackers found matching your criteria.
                      </Text>
                    </div>
                  ) : (
                    <Table
                      columns={tableColumns}
                      data={tableData}
                      displayStyle="divide-xy"
                      horizontalPadding={16}
                      verticalPadding={10}
                    />
                  )}
                  <div
                    className="flex items-center justify-between mt-4"
                    style={{ paddingBottom: "16px" }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-text-secondary">
                        Showing {paginationRange}
                      </span>
                      <span className="text-text-secondary">
                        Items per page:
                      </span>
                      <Select
                        size="sm"
                        onChange={(e) => {
                          setItemsPerPage(parseInt(e.target.value));
                          setSkipped(0);
                        }}
                        value={itemsPerPage}
                        disabled={fetchingHackers}
                      >
                        <Select.Option value={10}>10</Select.Option>
                        <Select.Option value={20}>20</Select.Option>
                        <Select.Option value={30}>30</Select.Option>
                        <Select.Option value={40}>40</Select.Option>
                        <Select.Option value={50}>50</Select.Option>
                      </Select>
                    </div>
                    <ButtonGroup>
                      <Button
                        size="sm"
                        disabled={skipped === 0}
                        onClick={() => handleFetchMoreHackers(false)}
                      >
                        <Icon
                          name="chevron-left"
                          color="#000"
                          className="w-4 h-4"
                        />
                      </Button>
                      <Button
                        size="sm"
                        disabled={
                          hackerProfiles.length === 0 ||
                          skipped + hackerProfiles.length >= totalCount
                        }
                        onClick={() => handleFetchMoreHackers(true)}
                      >
                        <Icon
                          name="chevron-right"
                          color="#000"
                          className="w-4 h-4"
                        />
                      </Button>
                    </ButtonGroup>
                  </div>
                </>
              </div>
            </Container>
          )}
          <Overlay open={overlayOpen} onClose={() => setOverlayOpen(false)}>
            <div>
              <Document
                file={selectedPDFUri}
                onLoadSuccess={({ numPages }) => setNumPDFPages(numPages)}
                onLoadError={(e) => {
                  errorToast(e.message);
                  setOverlayOpen(false);
                }}
                externalLinkTarget="_blank"
                loading="Loading PDF..."
              >
                <Page pageNumber={pagePDFNumber} />
              </Document>
              <PDFPagintation>
                <EmptyFilterTitleText
                  style={{ color: globalConstants?.color?.white as string }}
                >
                  {pagePDFNumber} of {numPDFPages}
                </EmptyFilterTitleText>
                <OverlayButtonGroup>
                  <Button
                    color="secondary"
                    leadingIcon={
                      <Icon
                        name={
                          favoritedHackerMap[selectedHackerId]
                            ? "selected-star"
                            : "not-selected-star"
                        }
                        size="12px"
                      />
                    }
                    onClick={() => onFavorite(selectedHackerId)}
                    size="sm"
                  >
                    {favoritedHackerMap[selectedHackerId] ? "Unstar" : "Star"}
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => window.open(selectedPDFUri, "_blank")}
                    size="sm"
                  >
                    Download
                  </Button>
                  <ButtonGroup>
                    <Button
                      size="sm"
                      disabled={pagePDFNumber === 1}
                      onClick={() =>
                        setPagePDFNumber(Math.max(0, pagePDFNumber - 1))
                      }
                    >
                      <Icon
                        name="chevron-left"
                        color="#000"
                        className="w-4 h-4"
                      />
                    </Button>
                    <Button
                      size="sm"
                      disabled={pagePDFNumber >= (numPDFPages ?? 1)}
                      onClick={() =>
                        setPagePDFNumber(
                          Math.min(numPDFPages ?? 1, pagePDFNumber + 1)
                        )
                      }
                    >
                      <Icon
                        name="chevron-right"
                        color="#000"
                        className="w-4 h-4"
                      />
                    </Button>
                  </ButtonGroup>
                </OverlayButtonGroup>
              </PDFPagintation>
            </div>
          </Overlay>
        </>
      ) : (
        <Container>
          <Text type="subtitle" style={{ color: "#666" }}>
            {hasPostEventResumeAccess && !isPostEvent
              ? "You will have access to resumes after the event. Please contact your Hack The North Sponsorship representative to discuss getting pre-event resume access."
              : "You do not have access to resumes. Please contact your Hack The North Sponsorship representative to discuss getting resume access."}
          </Text>
        </Container>
      )}
    </>
  );
};

// const DropdownButtonText = styled(Text)`
//   font-size: 13px !important;
//   font-weight: 500;
//   color: ${globalConstants?.color?.textBody as string};
// `;

// const DropdownFilters = styled(Flex)`
//   column-gap: 4px;
// `;

const GradYearFilterSelect = styled(Flex).attrs({
  row: true,
  justify: "space-between",
  align: "center",
})`
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: ${(globalConstants?.color?.textTertiary as string) ?? ""};
  row-gap: 10px;
`;

const OverlayButtonGroup = styled(Flex).attrs({
  align: "center",
})`
  column-gap: 8px;
`;

const PDFPagintation = styled(Flex).attrs({
  align: "center",
  justify: "space-between",
})`
  margin-top: 10px;
`;

const Container = styled(Flex).attrs({})`
  width: 100%;
  height: calc(100vh - ${TOPNAVBAR_HEIGHT}px);
  padding: 42px;
`;

const FilterContainer = styled(Flex).attrs({
  align: "center",
})`
  column-gap: 25px;
`;

// const FilterDropdown = styled(DropdownMenu)<{ selectWidth: number }>`
//   & * {
//     max-width: 100%;
//   }

//   & select {
//     width: ${({ selectWidth }) => selectWidth}px;
//   }
// `;

const SearchContainer = styled(Flex).attrs({ align: "center" })`
  column-gap: 5px;
`;

const EmptyFilterDescText = styled.p`
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #000;
  text-align: left;
`;

const EmptyFilterTitleText = styled.p`
  font-weight: 700;
  font-size: 14px;
  line-height: 150%;
  color: ${(globalConstants?.color?.textBody as string) ?? ""};
`;

const HoverTooltip = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const StatusCircle = styled.span<{ color: string }>`
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  min-width: 120px;
  max-width: 250px;
  width: max-content;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
  transition: opacity 0.3s, visibility 0.3s;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
  }
`;

const HoverTooltipWithTooltip = styled(HoverTooltip)`
  &:hover ${TooltipText} {
    visibility: visible;
    opacity: 1;
  }
`;

export default SponsorRecruitment;
